interface IPermissionError extends Error {
	permissionName: string;
	originalError: unknown;
	denied?: boolean;
}

export class PermissionError extends Error implements IPermissionError {
	readonly name = 'PermissionError';
	readonly permissionName: string;
	readonly originalError: unknown;
	readonly denied?: boolean;

	constructor({ permissionName, originalError, denied }: Omit<IPermissionError, 'name' | 'message'>) {
		super(`Permission ${denied ? 'denied' : 'error'} for '${permissionName}'`);
		this.permissionName = permissionName;
		this.originalError = originalError;
		this.denied = denied;
	}
}

async function isPermissionDenied(permissionName: string): Promise<boolean> {
	// casting to support non-standard permissions
	const name = permissionName as unknown as PermissionName;
	// in case this is run in a non-supporting browser it will throw
	const permission = await navigator.permissions.query({ name });
	return permission.state === 'denied';
}

export async function runWithPermission<T>(permissionName: string, promise: Promise<T>): Promise<T> {
	try {
		return await promise;
	} catch (error) {
		let denied;
		try {
			denied = await isPermissionDenied(permissionName);
		} catch (permissionError) {
			throw new PermissionError({ permissionName, originalError: permissionError });
		}
		throw new PermissionError({ permissionName, originalError: error, denied });
	}
}
